import React, { useRef } from 'react';
import { logger } from '@zep/core-logger';
import { FeatureFlagProvider } from '@zep/feature-flag';
import { getDeviceId } from '@zep/session-id';
import { useUser } from '@/lib/auth';
import { FEATURE_FLAG_ENVIRONMENT, FEATURE_FLAG_NAMESPACE, FEATURE_FLAG_SERVER_URL } from '../consts/featureFlag';

/**
 * quiz-app 전용 FeatureFlagProvider wrapper.
 * next/dynamic으로 CSR-only 로드되어 @flipt-io/flipt-client-js WASM이
 * 서버/클라이언트 webpack 빌드에서 완전히 제외됩니다.
 *
 * entityId 결정 순서:
 * 1. user.quizUserId (로그인 시 — CSR에서 결정)
 * 2. ssrEntityId (SSR에서 전달된 entityId — 초기값으로 캡처)
 * 3. getDeviceId() (CSR에서 쿠키/localStorage 기반)
 *
 * ssrEntityId: getServerSideProps에서 세션 기반으로 결정한 entityId.
 * 로그인 유저의 경우 quizUserId를, 비로그인의 경우 deviceId를 전달할 수 있습니다.
 */
export default function QuizFeatureFlagProvider({
  children,
  ssrEntityId,
  ssrFlags,
  ssrVariants
}: {
  children: React.ReactNode;
  ssrEntityId?: string;
  ssrFlags?: Record<string, boolean>;
  ssrVariants?: Record<string, string>;
}) {
  const {
    user
  } = useUser();
  const deviceId = useRef(ssrEntityId || getDeviceId() || '').current;
  const userId = user?.quizUserId || '';
  return <FeatureFlagProvider
  // Flipt 가 죽어도 평가는 기본값으로 degrade 해서 화면은 멀쩡하다. 그래서
  // 계측하지 않으면 장애가 조용히 지나가고, 실험 분석에서 그 구간이 통째로
  // 대조군처럼 보인다. fingerprint 를 고정해 알림 한 줄로 묶는다.
  // tag 가 있어야 Sentry alert 가 이 에러만 걸러낸다 — fingerprint 는 이슈를
  // 묶을 뿐 alert 조건으로 쓸 수 없다.
  onError={error => logger.captureError(error, {
    fingerprint: 'feature-flag-init',
    // dev 는 크롤러·자동화 실패가 대부분이라 warning 으로 낮춘다.
    level: process.env.NEXT_PUBLIC_STAGE === 'PRD' ? 'error' : 'warning',
    tags: {
      scope: 'feature-flag'
    }
  })}
  // 브라우저 비호환으로 초기화를 건너뛴 경우(구형 vendor 의 externref 미지원,
  // WebKit 16.3 이하의 WASM 런타임 버그)는 에러가 아니라 알려진 한계라 Sentry 대신
  // debug 로 남긴다. 브라우저·OS·UA 는 QuizAppLoggerInitializer 가 user property 로
  // 이미 붙이고(이 Provider 보다 먼저 렌더되는 형제다) 여기선 reason 만 넣는다 —
  // play 는 UA 를 형제가 아닌 부모 effect 에서 넣어 순서가 뒤집히므로 직접 싣는다.
  onInitializationSkipped={reason => logger.debug('feature-flag: 초기화 생략', {
    reason
  })} serverUrl={FEATURE_FLAG_SERVER_URL} namespace={FEATURE_FLAG_NAMESPACE} environment={FEATURE_FLAG_ENVIRONMENT} userId={userId} deviceId={deviceId} ssrFlags={ssrFlags} ssrVariants={ssrVariants} data-sentry-element="FeatureFlagProvider" data-sentry-component="QuizFeatureFlagProvider" data-sentry-source-file="QuizFeatureFlagProvider.tsx">
      {children}
    </FeatureFlagProvider>;
}